import invariant from 'tiny-invariant'
import type { RegistrationSource } from '@repo/auth-utils'

const googleOneTapLoginClientId =
  process.env.NEXT_PUBLIC_GOOGLE_ONE_TAP_LOGIN_CLIENT_ID ||
  process.env.VITE_GOOGLE_ONE_TAP_LOGIN_CLIENT_ID
const googleOneTapLoginRedirectUrl =
  process.env.NEXT_PUBLIC_GOOGLE_ONE_TAP_LOGIN_REDIRECT_URL ||
  process.env.VITE_GOOGLE_ONE_TAP_LOGIN_REDIRECT_URL

invariant(
  googleOneTapLoginClientId,
  'GOOGLE_ONE_TAP_LOGIN_CLIENT_ID must be set inside google-one-tap-login',
)
invariant(
  googleOneTapLoginRedirectUrl,
  'GOOGLE_ONE_TAP_LOGIN_REDIRECT_URL must be set inside google-one-tap-login',
)
const resolvedGoogleOneTapLoginRedirectUrl = googleOneTapLoginRedirectUrl

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          cancel: () => void
        }
      }
    }
  }
}

interface GoogleOneTapWidgetProps {
  shouldRedirect?: boolean
  redirectUrl?: string
  /**
   * When true, Google silently authenticates a returning user with no
   * interaction and (in redirect mode) POSTs the credential to `data-login_uri`,
   * causing an automatic redirect.
   */
  autoSelect?: boolean
  registrationSource?: RegistrationSource
}

export function buildGoogleOneTapLoginUrl({
  redirectUrl,
  registrationSource,
}: Pick<GoogleOneTapWidgetProps, 'redirectUrl' | 'registrationSource'>) {
  const url = new URL(resolvedGoogleOneTapLoginRedirectUrl)
  if (redirectUrl) {
    url.searchParams.set('redirect', redirectUrl)
  }
  if (registrationSource) {
    url.searchParams.set('registration_source', registrationSource.toLowerCase())
  }

  return url.toString()
}

/**
 * The raw Google Identity Services `g_id_onload` element. The caller must load
 * the GSI script (see {@link googleOneTapScriptUrl}); for a ready-to-mount
 * component that does that for you, use `GoogleOneTapLogin`.
 */
export const GoogleOneTapWidget: React.FC<GoogleOneTapWidgetProps> = ({
  shouldRedirect = true,
  redirectUrl,
  autoSelect = true,
  registrationSource,
}) => {
  return (
    <div
      id="g_id_onload"
      data-use_fedcm_for_prompt="true"
      data-client_id={googleOneTapLoginClientId}
      data-login_uri={
        shouldRedirect ? buildGoogleOneTapLoginUrl({ redirectUrl, registrationSource }) : ''
      }
      data-cancel_on_tap_outside="false"
      data-itp_support="true"
      data-auto_select={autoSelect ? 'true' : 'false'}
    />
  )
}

export const googleOneTapScriptUrl = `https://accounts.google.com/gsi/client`
